import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, first, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { policiesQuery, PolicySelectors } from '@amfam/policy/data-access';
import { AutoPolicy, GracePeriodDays, RiskModel } from '@amfam/policy/models';
import {
  fromVehicleActions,
  NewVehicle,
  VehicleFeaturesModel,
  vehicleQuery,
  VehicleService
} from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  DsModalService,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService,
  ValidationService
} from '@amfam/ui-kit';

import * as VehicleFeatureActions from '../+state/add-vehicle-feature.actions';
import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import { addVehicleExpectationSettingNotification } from '../+state/add-vehicle-feature.actions';
import * as addVehicleFeatureSelector from '../+state/add-vehicle-feature.selectors';
import { VinContent } from '../models';
import { AddVehicleFeatureService } from '../services/add-vehicle-feature.service';

@Component({
  selector: 'ds-vehicle-vin-odo',
  templateUrl: './vehicle-vin-odo.component.html',
  styleUrls: ['./vehicle-vin-odo.component.scss'],
  standalone: false
})
export class VehicleVinOdoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() exitEvent = new EventEmitter<boolean>();

  hasError$: Observable<boolean> = this.store.select(vehicleQuery.selectHasError);

  errorList: Array<ErrorObj> = [];

  errorMessage: string;

  sameVINErrorMessage: string;

  gracePeriodDays$: Observable<string>;

  years$ = this.store.select(vehicleQuery.selectYears);

  // Form elements
  yearVinForm: UntypedFormGroup;

  yearCtrl: AbstractControl;

  vinCtrl: AbstractControl;

  // Grace period
  displayPolicyGracePeriod = false;

  policy: AutoPolicy;
  public autoPolicies$ = this.store.select(policiesQuery.selectAutoPolicies);

  public isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public vehicleId: string;

  // Keeps track of whether an API call is in-flight
  loading$: Observable<boolean> = this.store.select(vehicleQuery.selectLoading);
  isSameVin = false;
  isPartner: boolean;
  classicOnlyAutoPolicyOrCompOnlyAdv = false;
  enabledMsrp = this.featureFlagService.isEnabled('msrp');

  public activeAgents$ = this.store
    .select(
      createSelector(
        addVehicleFeatureSelector.selectAllActiveAgents,
        agentQuery.getAreAllAgentsLoaded,
        (agents, loaded) => ({ agents, loaded })
      )
    )
    .pipe(
      filter(data => !!data.loaded),
      map(data => data.agents)
    );

  public getAdvanceAutoPolicies$ = this.store.select(PolicySelectors.selectAdvanceAutoPolicies);
  public userState$ = this.store.select(userQuery.selectUserState);
  findVinFlowModalId = this.modalService.createModalId('findVinFlowModalId');
  vinContent: VinContent;
  showMsrp$: Observable<boolean>;
  vehicleDetails: { newVehicle: NewVehicle; features: VehicleFeaturesModel[] };
  errors: Map<string, string> = new Map<string, string>([
    ['400003', "Vehicle Year doesn't match VIN"],
    ['400004', "Vehicle Year doesn't match VIN"],
    [
      '*',
      'We’re not currently able to give a quote for this vehicle. To get a quote, please contact your agent.'
    ]
  ]);

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  private replaceVehicleRisks$: Observable<RiskModel[]> = this.store.select(
    addVehicleFeatureSelector.selectVehicleRisks
  );

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    public roleService: ImpersonateRolesService,
    private spinnerService: LoadingSpinnerService,
    private copyService: CopyService,
    private modalService: DsModalService,
    private analyticsFacade: AnalyticsFacade,
    private featureFlagService: FeatureFlagService,
    private route: ActivatedRoute,
    private dockingBarService: DockingBarService,
    private pageTitle: PageTitleService,
    private vehicleService: VehicleService,
    private addVehicleService: AddVehicleFeatureService
  ) {}

  ngOnInit() {
    this.isPartner$.pipe(takeUntil(this.stop$)).subscribe(isPartner => {
      this.isPartner = isPartner;
    });
    this.buildForm();

    // this.handleError();
    // Listen for valid responses to the year-vin - if we have a valid response, forward to the next route.
    const replaceTxt = this.copyService.getCopy('policy.replaceVehicle');
    this.route.params.pipe(takeUntil(this.stop$)).subscribe(params => {
      if (params && params['id'] > 0) {
        this.vehicleId = params['id'];
        this.pageTitle.set(replaceTxt);
        this.dockingBarService.registerHeading(replaceTxt);
      }
    });

    combineLatest([
      this.store.select(vehicleQuery.selectNewVehicle),
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(PolicySelectors.selectHasClassicAutoPolicies),
      this.store.select(PolicySelectors.selectHasAdvanceAutoPolicies),
      this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage)
    ])
      .pipe(
        filter(([newVehicle]) => this.yearCtrl.enabled && this.vinCtrl.enabled && !!newVehicle),
        first()
      )
      .subscribe(
        ([
          newVehicle,
          isPartner,
          hasClassicAutoPolicy,
          hasAdvanceAutoPolicy,
          hasOnlyPolicyComprehensiveCoverage
        ]) => {
          if (newVehicle) {
            this.store.dispatch(
              fromVehicleActions.SetCriteria({
                year: _get(this.yearCtrl, 'value'),
                vin: _get(this.vinCtrl, 'value'),
                odometer: '0'
              })
            );

            this.store.dispatch(
              VehicleInfoActions.setVehicleYearVinOdo({
                vehicleInfo: {
                  year: _get(this.yearCtrl, 'value'),
                  vin: _get(this.vinCtrl, 'value'),
                  odoMeter: '0'
                }
              })
            );

            const routePath = '/policies/add-vehicle/';

            const advanceCompOnly =
              hasAdvanceAutoPolicy && !isPartner && hasOnlyPolicyComprehensiveCoverage;

            const classicOnlyAutoPolicy =
              hasClassicAutoPolicy && !hasAdvanceAutoPolicy && !isPartner;
            if (classicOnlyAutoPolicy || advanceCompOnly) {
              this.classicOnlyAutoPolicyOrCompOnlyAdv = true;
            }

            this.store.dispatch(
              fromRouterActions.Go({
                path: [
                  hasClassicAutoPolicy && this.vehicleId
                    ? `${routePath}agent-help/${this.vehicleId}`
                    : this.classicOnlyAutoPolicyOrCompOnlyAdv
                      ? `${routePath}agent-help`
                      : `${routePath}primary-use-info`
                ]
              })
            );
          }
        }
      );

    this.gracePeriodDays$ = this.store.select(userQuery.selectUserStateCode).pipe(
      takeUntil(this.stop$),
      map(userState => {
        const isExtendedGracePeriodState = !!this.copyService
          .getCopy('policy.extendedGracePeriodStates')
          .find(state => state === userState);
        return !isExtendedGracePeriodState
          ? GracePeriodDays.REGULAR // 14
          : GracePeriodDays.EXTENDED; // 30
      })
    );

    this.buildForm();
    this.vinContent = this.copyService.getCopy('policy.findVIN');
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'Start' }
      })
    );
  }

  submit() {
    if (this.enabledMsrp) {
      let agent;
      this.activeAgents$.pipe(take(1)).subscribe(data => {
        agent = data;
      });

      const make = _get(this.yearVinForm, 'controls.make.value');
      const model = _get(this.yearVinForm, 'controls.model.value');
      const msrp = this.vehicleDetails?.newVehicle?.msrp;

      if (make === 'other' || model === 'other' || !msrp) {
        this.notify(agent[0]);
        this.store.dispatch(
          VehicleInfoActions.addVehicleSendAnalytics({
            payload: { buttonAnalyticName: 'Other:SendToAgent' }
          })
        );
        this.store.dispatch(VehicleFeatureActions.addVehicleOtherExitFlow());
      } else {
        this.dispatchValidYearVin();
      }
    } else {
      this.dispatchValidYearVin();
    }
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  verifyVin() {
    const vinValue = _get(this.yearVinForm, 'controls.vin.value', '');
    if (vinValue.length === 17) {
      this.replaceVehicleRisks$.pipe(take(1)).subscribe(vehicle => {
        const matchingVehicle = vehicle.find(
          vehicleVINRisks => _get(vehicleVINRisks, 'vin') === vinValue.toUpperCase()
        );
        if (matchingVehicle) {
          this.isSameVin = true;
          this.sameVINErrorMessage = this.copyService.get(
            'policy',
            'sameVINReplaceVehicleErrorMessage'
          );
        } else {
          this.isSameVin = false;
          this.sameVINErrorMessage = '';
        }
      });
    }
  }

  toggleGracePeriod() {
    this.displayPolicyGracePeriod = !this.displayPolicyGracePeriod;
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { buttonAnalyticName: 'Start:CoverageDetails' }
      })
    );
  }

  findVIN(e) {
    e.preventDefault();
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: {
          buttonAnalyticName: 'Start:FindVIN'
        }
      })
    );

    this.openModal().then(() => {
      setTimeout(() => {
        this.store.dispatch(
          VehicleInfoActions.addVehicleSendAnalytics({
            payload: {
              pageAnalyticName: 'FindVIN'
            }
          })
        );
      }, 0);
    });
  }

  onVehicleDetailsLoaded(vehicleDetails) {
    if (vehicleDetails?.newVehicle?.series === 'other') vehicleDetails.newVehicle.series = '';
    this.vehicleDetails = vehicleDetails;
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    this.spinnerService.stop();
  }

  private dispatchValidYearVin() {
    this.analyticsFacade.trackEvent({
      event: 'vin_not_found_complete',
      form_name: 'Vin Not Found',
      method: 'Add Vehicle'
    });
    this.store.dispatch(
      fromVehicleActions.LookupVinYearSuccess({
        ...this.vehicleDetails,
        criteria: { ...this.vehicleDetails.newVehicle }
      })
    );
  }

  private buildForm() {
    const formGroup = {
      year: ['', Validators.required],
      vin: [
        '',
        Validators.compose([
          ValidationService.vinFormatValidator,
          ValidationService.vinRequiredValidator
        ])
      ]
    };
    this.yearVinForm = this.formBuilder.group(formGroup);
    this.yearCtrl = this.yearVinForm.controls?.year;
    this.vinCtrl = this.yearVinForm.controls?.vin;
    this.errorList = [];
    this.showMsrp$ = combineLatest([this.vinCtrl.valueChanges, this.yearCtrl.valueChanges]).pipe(
      tap(() => (this.errorMessage = '')),
      filter(([vin, year]) => !!vin && !!year && this.vinCtrl.valid && this.yearCtrl.valid),
      switchMap(([vin, year]) =>
        this.vehicleService
          .getVehicleByVinAndYear({
            year: year,
            vin: vin
          })
          .pipe(
            tap(vehicle => {
              this.errorMessage = null;
              this.vehicleDetails = vehicle;
            }),
            map(vehicle => (vehicle ? 1 : 0)),
            catchError(err => {
              this.setError(err, year);
              this.vehicleDetails = null;
              return of(-1);
            })
          )
      ),
      map(success => {
        const year = this.yearCtrl.value;
        return success === 0 && year >= new Date().getFullYear();
      }),
      catchError(err => {
        this.setError(err, this.yearCtrl?.value);
        this.vehicleDetails = null;
        return of(false);
      })
    );

    this.showMsrp$
      .pipe(
        takeUntil(this.stop$),
        tap(showMsrp => {
          if (showMsrp)
            this.analyticsFacade.trackEvent({
              event: 'vin_not_found_start',
              form_name: 'Vin Not Found',
              method: 'Add Vehicle'
            });
        })
      )
      .subscribe();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setError(err: any, _year: number) {
    this.errorMessage = null;
    const errors = err?.status?.errors;
    if (errors?.length > 0 && this.errors.has(String(errors[0].code))) {
      this.errorMessage = this.errors.get(String(errors[0].code));
      return;
    }
    this.errorMessage = this.errors.get('*');
  }

  private openModal = async () => {
    this.modalService.open(this.findVinFlowModalId);
  };

  private notify(agent: Agent) {
    this.spinnerService.start({ blockActions: true });
    this.autoPolicies$
      .pipe(
        filter(policies => !!policies && policies.length > 0),
        first(),
        tap(policies => {
          const policyNumbers = policies
            .filter(policy => policy.assignedProducer.producerIdNum === agent.id)
            .map(p => p.policyNumber);

          this.store.dispatch(
            addVehicleExpectationSettingNotification({
              payload: {
                agent,
                policyNumber: policyNumbers.length > 0 ? policyNumbers.join(',') : policyNumbers[0]
              }
            })
          );
        })
      )
      .subscribe();
  }
}
